import { toAbsolutePath, isHover, shiftOverlappingPoints, calmFlyTo } from '../../helpers.js'
import center from  '@turf/center'
import { bus } from '../../bus.js'

export default class LayerMarkers {
  constructor (name, map) {
    this.name = name
    this.map = map
    this.defaultGeojson = { 'type': 'FeatureCollection', 'features': [] }
    this.handleClusterClick = this.handleClusterClick.bind(this)
    this.handleMarkerClick = this.handleMarkerClick.bind(this)
    this.handleMarkerMouseenter = this.handleMarkerMouseenter.bind(this)
    this.handleMarkerMouseleave = this.handleMarkerMouseleave.bind(this)
    this.setActive = this.setActive.bind(this)
    this.clearActive = this.clearActive.bind(this)
    this.flyTo = this.flyTo.bind(this)

    // Add listeners to set/unset active markers from other components
    bus.on('map:setactive', this.setActive)
    bus.on('map:clearactive', this.clearActive)
    bus.on('map:flyto', this.flyTo)
  }

  create (geojson) {
    this.map.addSource(this.name, {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterRadius: 30,
      clusterMaxZoom: 13
    })

    this.map.addLayer({
      id: `${this.name}-clusters`,
      type: 'circle',
      source: this.name,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#fff',
        'circle-stroke-color': '#000',
        'circle-radius': 12,
        'circle-stroke-width': 1
      }
    })

    this.map.addLayer({
      id: `${this.name}-cluster-count`,
      type: 'symbol',
      source: this.name,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Theinhardt Bold'],
        'text-size': 13,
        'text-offset': [-0.03,-0.06],
        'text-allow-overlap': true
      },
      paint: {
        'text-color': '#000'
      }
    })

    this.map.addLayer({
      id: `${this.name}-markers`,
      type: 'symbol',
      source: this.name,
      filter: [
        '!has', 'point_count'
      ],
      layout: {
        // Set icon image based on property.color value
        'icon-image': [
          'match', 
          ['get', 'color'],
          'red', 'map-marker_red',
          'blue', 'map-marker_blue',
          'green', 'map-marker_green',
          'yellow', 'map-marker_yellow',
          'tan', 'map-marker_tan',
          'accent', 'map-marker_accent',
          'muted', 'map-marker_muted',
          'map-marker_white'
        ],
        'icon-size': 1,
        'icon-allow-overlap': true
      }
    })

    this.map.addLayer({
      id: `${this.name}-markers-hover`,
      type: 'symbol',
      source: this.name,
      filter: false,
      layout: {
        'icon-image': 'map-marker_red',
        'icon-size': 1,
        'icon-allow-overlap': true
      }
    })

    // Separate active marker source/layer that does not cluster.
    // This is used to highlight markers from within the dom, such
    // as in guides or when hovering over grid items.
    this.map.addSource(`${this.name}-active`, {
      type: 'geojson',
      data: geojson,
      cluster: false
    })

    this.map.addLayer({
      id: `${this.name}-markers-active`,
      type: 'symbol',
      source: `${this.name}-active`,
      filter: false,
      layout: {
        'icon-image': 'map-marker_red',
        'icon-size': 1,
        'icon-allow-overlap': true
      }
    })

    this.map.on('click', `${this.name}-clusters`, this.handleClusterClick)
    if (isHover.matches) {
      this.map.on('click', `${this.name}-markers`, this.handleMarkerClick)
    }
    this.map.on('mousemove', `${this.name}-markers`, this.handleMarkerMouseenter)
    this.map.on('mouseleave', `${this.name}-markers`, this.handleMarkerMouseleave)
    this.map.on('mouseenter', `${this.name}-clusters`, this.handleMarkerMouseenter)
    this.map.on('mouseleave', `${this.name}-clusters`, this.handleMarkerMouseleave)
  }

  handleClusterClick (e) {
    const features = this.map.queryRenderedFeatures(e.point, {
      layers: [`${this.name}-clusters`]
    })
    const clusterId = features[0].properties.cluster_id
    this.map.getSource(this.name).getClusterExpansionZoom(
      clusterId,
      (err, zoom) => {
        if (err) return
     
        this.map.flyTo({
          center: features[0].geometry.coordinates,
          zoom: zoom
        })
      }
    )
  }

  handleMarkerClick (e) {
    if (e.features.length > 0) {
      const linkTo = e.features[0]?.properties?.linkTo
      if (linkTo) {
        window.GPLA_APP.go(toAbsolutePath(linkTo))
        return
      }
      const scrollTo = e.features[0]?.properties?.scrollTo
      if (scrollTo && document.getElementById(scrollTo)) {
        document.getElementById(scrollTo).scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  handleMarkerMouseenter (e) {
    this.map.getCanvas().style.cursor = 'pointer'
    
    // Set hover feature state
    const uuid = e?.features?.[0]?.properties?.uuid
    this.setHover(uuid)
  }

  handleMarkerMouseleave (e) {
    this.map.getCanvas().style.cursor = ''
    this.clearHover()
  }

  setHover (uuid) {
    if (!uuid || !this.map.getLayer(`${this.name}-markers-hover`)) return
    this.map.setFilter(`${this.name}-markers-hover`, ['==', 'uuid', uuid])
  }

  clearHover () {
    if (!this.map.getLayer(`${this.name}-markers-hover`)) return
    this.map.setFilter(`${this.name}-markers-hover`, false)
  }

  setActive (uuids) {
    if (!uuids || !this.map.getLayer(`${this.name}-markers-active`)) return
    this.map.setFilter(`${this.name}-markers-active`, ['in', 'uuid', ...uuids])
  }

  clearActive () {
    if (!this.map.getLayer(`${this.name}-markers-active`)) return
    this.map.setFilter(`${this.name}-markers-active`, false)
  }

  flyTo (uuids = []) {
    const features = (this.map.getSource(this.name)?._data?.features || [])
      .filter(feature => uuids.includes(feature.properties.uuid))
    if (features.length === 0) return
    const geoCenter = center({ type: 'FeatureCollection', features })
    calmFlyTo(geoCenter?.geometry?.coordinates, this.map)
  }

  update (geojson, placement) {
    const transformedGeojson = shiftOverlappingPoints(geojson)
    if (!this.map.getSource(this.name)) {
      this.create(transformedGeojson, placement)
      return
    }
    this.map.getSource(this.name).setData(transformedGeojson)
    // Also pass data to active source
    this.map.getSource(`${this.name}-active`).setData(transformedGeojson)  
  }

  // reset () {
  //   if (this.map.getSource(this.name)) {
  //     this.map.getSource(this.name).setData(this.defaultGeojson)    
  //   }
  // }
}